import { createContext, useContext, useState, useEffect, useRef } from "react";

// Blockly
import * as Blockly from "blockly";

// mui icons
import GridOnIcon from "@mui/icons-material/GridOn";
import GestureSharpIcon from "@mui/icons-material/GestureSharp";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LandslideOutlinedIcon from "@mui/icons-material/LandslideOutlined";

// services
import SimulatorService from "../../services/simulator.service";

// context
import { useAppContext } from "./AppContext";
import { usePlatformContext } from "./PlatformContext";

// utils
import { NotificationMessages } from "../utils/constants";
import TokenService from "../../services/token.service";
import { getSimulatorUrl, dispatchResizeEvent } from "../utils";

export const WorkspaceContext = createContext();

export const WorkspaceProvider = ({ children }) => {
    const {
        project,
        user,
        updateProject,
        isCoursewareProject,
        setProject,
        setChallengeList,
    } = usePlatformContext();
    const { setNotificationMessage } = useAppContext();

    // project state stuff
    const typingTimeoutRef = useRef(null);
    const [isProjectSaved, setIsProjectSaved] = useState(true);

    const getStartXml = () => {
        const newProjectXml = `<xml xmlns="https://developers.google.com/blockly/xml"></xml>`;
        if (project) {
            return project.content || newProjectXml;
        } else {
            return TokenService.getSavedXmlBlocks() || newProjectXml;
        }
    };

    const startBlocklyCodeState = {
        xml: getStartXml(),
        arduino: `void setup() {} void loop() {}`,
        javascript: ``,
    };

    const startBlocklyToolboxState = {
        open: true,
        type: "level_4",
    };

    const FreePlayEnvironments = [
        {
            type: "open",
            icon: GridOnIcon,
            title: "Empty Table",
        },
        {
            type: "line",
            icon: GestureSharpIcon,
            title: "Line Following",
        },
        {
            type: "obstacles",
            icon: CategoryOutlinedIcon,
            title: "Random Obstacles",
        },
        {
            type: "combo",
            icon: LandslideOutlinedIcon,
            title: "Line and Obstacles",
        },
    ];

    // refs
    const blocklyWorkspaceRef = useRef(null);
    const simulatorFrameRef = useRef(null);

    // loading
    const [isLoading, setIsLoading] = useState(false);

    // blockly states
    const [blocklyCode, setBlocklyCode] = useState(startBlocklyCodeState);
    const [blocklyToolbox, setBlocklyToolbox] = useState(
        startBlocklyToolboxState
    );

    // simulator states
    const [simulatorEnvDrawerOpen, setSimulatorEnvDrawerOpen] = useState(false);
    const [simulatorFullscreen, setSimulatorFullscreen] = useState(false);
    const [simulatorOpen, setSimulatorOpen] = useState(false);
    const [simulatorReady, setSimulatorReady] = useState(false);
    const [simulatorRunning, setSimulatorRunning] = useState(false);
    const [simulatorAddons, setSimulatorAddons] = useState([]);
    const [simulatorEnvironment, setSimulatorEnvironment] = useState();

    // serial monitor states
    const [serialMonitorOpen, setSerialMonitorOpen] = useState(false);
    const [serialMonitorData, setSerialMonitorData] = useState([]);

    // confetti
    const [showConfetti, setShowConfetti] = useState(false);

    // challenge mode
    const [challengeIndex, setChallengeIndex] = useState();
    const [challengeData, setChallengeData] = useState();

    useEffect(() => {
        if (isCoursewareProject) {
            window.addEventListener("message", handleLMSMessage);
            window.parent.postMessage({ type: "request_courseware_data" }, "*");
            return () =>
                window.removeEventListener("message", handleLMSMessage);
        }
    }, [isCoursewareProject]);

    useEffect(() => {
        const addons = SimulatorService.checkAddons(
            simulatorFrameRef,
            blocklyCode.xml,
            blocklyWorkspaceRef,
            simulatorOpen
        );
        if (addons.join("-") !== simulatorAddons.join("-")) {
            setSimulatorAddons(addons);
        }
    }, [blocklyCode, simulatorReady, simulatorOpen]);

    const handleLMSMessage = (event) => {
        if (event.source !== window.parent) return;
        const message = event.data;

        if (message.code) {
            try {
                const xml = Blockly.utils.xml.textToDom(message.code);
                blocklyWorkspaceRef.current.clear();
                Blockly.Xml.domToWorkspace(xml, blocklyWorkspaceRef.current);
            } catch (e) {
                setNotificationMessage({
                    message: NotificationMessages.blockly.invalidXml,
                    type: "error",
                    duration: 4000,
                });
            }
        }

        if (message.challenge_id) {
            initializeSimulator();
            setSimulatorOpen(true);
            setChallengeList([{ id: message.challenge_id }]);
            setChallengeIndex(1);
        }
    };

    const handleBlocklyChange = (newCodeState) => {
        setBlocklyCode(newCodeState);

        if (project && user) {
            setIsProjectSaved(false);

            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            typingTimeoutRef.current = setTimeout(() => {
                saveProjectChange(newCodeState.xml);
            }, 3000);
        } else if (project && isCoursewareProject) {
            // do nothing for now
            //console.log("courseware, dont save");
        } else {
            TokenService.setSavedXmlBlocks(newCodeState.xml);
        }
    };

    const saveProjectChange = async (projectXml) => {
        setIsProjectSaved(false);
        try {
            await updateProject(projectXml);
            setIsProjectSaved(true);
        } catch (e) {
            setNotificationMessage({
                message:
                    "Unable to save project. Please check your internet connection",
                type: "error",
                duration: 6000,
            });
            console.error(e);
        }
    };

    const handleSimEvent = (event) => {
        if (!event || event.close) {
            setSimulatorRunning(false);
            return;
        }
        if (event.err) {
            setSimulatorRunning(false);
            setNotificationMessage({
                message: event.err.toString(),
                type: "error",
                duration: 4000,
            });
        }
        if (event.serialMessage) {
            logToSerialMonitor(event.serialMessage);
        }
        // TODO: Challenge Events
    };

    const logToSerialMonitor = (message) => {
        message = message.toString();
        const lines = message.split("\n");
        const [firstLine, ...newLines] = lines;

        setSerialMonitorData((prevData) => {
            const updatedData = [...prevData];

            if (updatedData.length > 0) {
                updatedData[updatedData.length - 1] += firstLine;
            } else {
                updatedData.push(firstLine);
            }

            updatedData.push(...newLines);

            return updatedData.length > 500
                ? updatedData.slice(-500)
                : updatedData;
        });
    };

    const initializeSimulator = () => {
        if (!simulatorFrameRef.current.src) {
            simulatorFrameRef.current.src = getSimulatorUrl();
        }
        dispatchResizeEvent(750);
        window.setTimeout(() => {
            blocklyWorkspaceRef.current.scrollCenter();
        }, 1500);
    };

    const startSimulator = () => {
        setSerialMonitorData(["\n> Connected to Simulator Serial Port", ""]);
        let program = blocklyCode.javascript;
        if (
            !blocklyCode.javascript.setupCode &&
            !blocklyCode.javascript.loopCode
        ) {
            setNotificationMessage({
                message: NotificationMessages.blockly.noBlocks,
                type: "info",
                duration: 4000,
            });
            return;
        }
        setSimulatorRunning(true);
        SimulatorService.startSimulation(
            simulatorFrameRef,
            program,
            handleSimEvent
        );
    };

    const stopSimulator = () => {
        setSimulatorRunning(false);
        SimulatorService.stopSimulation();
    };

    const resetSimulator = () => {
        setSimulatorRunning(false);
        SimulatorService.resetSimulation();
    };

    const providerValue = {
        // loading states
        isLoading,
        setIsLoading,
        isProjectSaved,

        // blockly states
        blocklyCode,
        setBlocklyCode,
        blocklyToolbox,
        setBlocklyToolbox,
        blocklyWorkspaceRef,

        // simulator states
        simulatorOpen,
        setSimulatorOpen,
        simulatorReady,
        setSimulatorReady,
        simulatorRunning,
        setSimulatorRunning,
        simulatorAddons,
        setSimulatorAddons,
        simulatorFrameRef,
        simulatorFullscreen,
        setSimulatorFullscreen,
        simulatorEnvDrawerOpen,
        setSimulatorEnvDrawerOpen,
        simulatorEnvironment,
        setSimulatorEnvironment,
        FreePlayEnvironments,
        challengeIndex,
        setChallengeIndex,
        challengeData,
        setChallengeData,

        // serial Monitor states
        serialMonitorData,
        setSerialMonitorData,
        serialMonitorOpen,
        setSerialMonitorOpen,

        // functions
        handleBlocklyChange,
        saveProjectChange,
        initializeSimulator,
        startSimulator,
        stopSimulator,
        resetSimulator,
        logToSerialMonitor,
        showConfetti,
        setShowConfetti,
    };

    return (
        <WorkspaceContext.Provider value={providerValue}>
            {children}
        </WorkspaceContext.Provider>
    );
};

export const useWorkspaceContext = () => {
    return useContext(WorkspaceContext);
};
